import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ExternalLink, Copy, Check, Tag, Percent, ShoppingBag, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const DealDialog = ({ deal, open, onOpenChange, storeName }) => {
  const [copied, setCopied] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(false);
  const { isAuthenticated, isFavorite, addFavorite, removeFavorite } = useAuth();

  if (!deal) return null;

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    setLoadingFavorite(true);
    try {
      if (isFavorite(deal.id)) {
        await removeFavorite(deal.id);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(deal.id);
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingFavorite(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(deal.couponCode);
      setCopied(true);
      toast.success('Coupon code copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGoToStore = () => {
    window.open(deal.storeUrl, '_blank');
    onOpenChange(false);
  };

  const savings = deal.originalPrice && deal.salePrice 
    ? (deal.originalPrice - deal.salePrice).toFixed(2) 
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#E53A7F] to-[#C42D69] px-6 py-4">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-bold flex items-center gap-2">
              <ShoppingBag size={20} />
              Deal Details
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6">
          {/* Product Image & Info */}
          <div className="flex gap-4 mb-6">
            <div className="relative w-32 h-32 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border">
              <img
                src={deal.image}
                alt={deal.title}
                className="w-full h-full object-contain p-2"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop';
                }}
              />
              {/* Favorite Button on Image */}
              <button
                onClick={handleToggleFavorite}
                disabled={loadingFavorite}
                className={`absolute top-1 right-1 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  isFavorite(deal.id) 
                    ? 'bg-red-500 text-white' 
                    : 'bg-white/90 text-gray-400 hover:text-red-500 hover:bg-white shadow-sm'
                }`}
                data-testid="dialog-favorite-btn"
              >
                {loadingFavorite ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Heart size={16} fill={isFavorite(deal.id) ? 'currentColor' : 'none'} />
                )}
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 text-base line-clamp-2 mb-2">
                {deal.title}
              </h3>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl font-bold text-[#E53A7F]">
                  ${deal.salePrice?.toFixed(2)}
                </span>
                {deal.originalPrice && (
                  <span className="text-gray-400 line-through text-sm">
                    ${deal.originalPrice.toFixed(2)}
                  </span>
                )}
              </div>
              {savings && (
                <div className="flex items-center gap-1 text-red-500 text-sm font-medium">
                  <Percent size={14} />
                  You save ${savings} ({deal.discount}% off)
                </div>
              )}
            </div>
          </div>

          {/* Add to Favorites Button */}
          <button
            onClick={handleToggleFavorite}
            disabled={loadingFavorite}
            className={`w-full mb-4 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all ${
              isFavorite(deal.id)
                ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
            }`}
            data-testid="dialog-favorite-btn-large"
          >
            {loadingFavorite ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Heart size={18} fill={isFavorite(deal.id) ? 'currentColor' : 'none'} />
            )}
            {isFavorite(deal.id) ? 'Remove from Favorites' : 'Add to Favorites'}
          </button>

          {/* Coupon Code Box */}
          <div className="bg-gray-50 border-2 border-dashed border-[#E53A7F] rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Use this coupon code:</p>
                <p className="text-xl font-mono font-bold text-[#E53A7F]">
                  {deal.couponCode}
                </p>
              </div>
              <button
                onClick={handleCopyCode}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  copied
                    ? 'bg-red-100 text-red-700'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {copied ? (
                  <>
                    <Check size={16} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Cashback Info */}
          {deal.cashback && (
            <div className="flex items-center gap-2 mb-4 text-sm">
              <Tag size={16} className="text-[#E53A7F]" />
              <span className="text-gray-600">{deal.cashback}</span>
            </div>
          )}

          {/* Store Info */}
          <div className="flex items-center justify-between py-3 border-t border-b mb-4">
            <span className="text-gray-500 text-sm">Store:</span>
            <span className="font-semibold text-gray-800">
              {storeName || deal.store?.charAt(0).toUpperCase() + deal.store?.slice(1)}
            </span>
          </div>

          {/* Go to Store Button */}
          <button
            onClick={handleGoToStore}
            className="w-full bg-[#E53A7F] text-white py-3.5 rounded-lg font-semibold text-base hover:bg-[#C42D69] transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-500/20"
          >
            <ExternalLink size={18} />
            Go to Store
          </button>

          <p className="text-center text-xs text-gray-400 mt-3">
            You will be redirected to {storeName || deal.store} to complete your purchase
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DealDialog;
